import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { session } from "../lib/session";
import { getWorkspace } from "../lib/workspaceStore";
import { api } from "../lib/api";

const callbackUrl = () => `${window.location.origin}/app/connections/callback`;

const pageHelp = {
  "/app/connections": {
    title: "Connection command center",
    body: "I can help unblock OAuth, find callback URLs, prepare manual setup notes, and decide what data OmniMark can use right now.",
    actions: [
      { label: "Open Connections", href: "/app/connections" },
      { label: "Open Campaigns", href: "/app/campaigns" },
    ],
  },
  "/app/ads": {
    title: "Ad pack help",
    body: "Tell me the product, offer, audience, and goal. I can help turn it into hooks, scripts, and campaign next steps.",
    actions: [
      { label: "Brand DNA", href: "/app/brand" },
      { label: "Campaigns", href: "/app/campaigns" },
    ],
  },
  "/app/dropshipping": {
    title: "Dropshipping help",
    body: "I can help scan products, pick a testing theme, connect suppliers, and decide which product deserves a fast page.",
    actions: [
      { label: "Connect suppliers", href: "/app/connections" },
      { label: "AI Studio", href: "/app/studio" },
    ],
  },
  "/app/studio/avatar": {
    title: "Avatar help",
    body: "I can help create an in-house avatar, pick a presenter, write a video brief, and send it into ads or campaigns.",
    actions: [
      { label: "Avatar Library", href: "/app/avatars" },
      { label: "Ad Creator", href: "/app/ads" },
    ],
  },
};

export default function OmniAssist() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I’m OmniAssist. I can help with setup, accounts, ads, dropshipping, avatars, and what to do next on each page.",
    },
  ]);
  const [input, setInput] = useState("");
  const [liveContext, setLiveContext] = useState(null);

  // Refresh the manifest periodically so assistant guidance follows deployed
  // capabilities and policy changes without a frontend release.
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await api.get("/api/omniassist/context");
        if (active) setLiveContext(response.data);
      } catch {
        // Keep the local fallback guidance when an older backend is deployed.
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 5 * 60 * 1000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const workspace = getWorkspace();
  const email = session.getEmail();
  const isMyValueWorkspace = /my value of life/i.test(workspace?.name || "");
  const help = pageHelp[location.pathname] || {
    title: "Site assistant",
    body: "I can explain this page, help with your workspace, and point you to the right OmniMark tool.",
    actions: [
      { label: "Dashboard", href: "/app" },
      { label: "Connections", href: "/app/connections" },
    ],
  };

  const contextLine = useMemo(() => {
    const name = workspace?.name || "No workspace selected";
    return `${email || "Signed out"} • ${name}`;
  }, [email, workspace?.name]);

  const quickReplies = useMemo(() => {
    const connectionPrompt = isMyValueWorkspace
      ? "Help me connect My Value of Life Facebook page"
      : "Help me connect my Facebook page";
    const connectionReplies = location.pathname === "/app/connections"
      ? [
          "Meta says URL blocked",
          "Where do I find my App ID?",
          "What can I do without OAuth?",
          "Give me the callback URL",
        ]
      : [];
    return [
      connectionPrompt,
      "What should I do next on this page?",
      "Help with my account or subscription",
      "Explain this page in simple steps",
      ...connectionReplies,
    ].slice(0, 6);
  }, [isMyValueWorkspace, location.pathname]);

  const capabilitySummary = useMemo(() => {
    const items = liveContext?.capabilities || [];
    return items.filter((item) => item.status !== "available").map((item) => `${item.name}: ${item.status.replaceAll("_", " ")}`);
  }, [liveContext]);

  function answer(text) {
    const lower = text.toLowerCase();
    const businessName = workspace?.name || "your business";
    if (lower.includes("what can you do") || lower.includes("capabilities") || lower.includes("tools")) {
      const items = liveContext?.capabilities || [];
      if (!items.length) return "I’m syncing with the OmniMark backend now. I can help with connections, ads, UGC scripts, avatars, video editing, campaigns, dropshipping, and exports.";
      const available = items.filter((item) => item.status === "available").map((item) => item.name).join(", ");
      const gated = items.filter((item) => item.status !== "available").map((item) => `${item.name} (${item.status.replaceAll("_", " ")})`).join(", ");
      return `OmniMark currently supports: ${available}.${gated ? ` Current limits: ${gated}.` : ""} Ask me about any one and I’ll give you the exact next step.`;
    }
    if (lower.includes("schedule") || lower.includes("post every") || lower.includes("automatically post")) {
      const scheduler = liveContext?.capabilities?.find((item) => item.key === "content_scheduler");
      if (scheduler?.status === "migration_pending") return "Recurring content is implemented, but the Supabase content-scheduler migration still needs to be applied before schedules can run. I’ll keep this marked pending rather than pretend it is live.";
      return "Create a recurring content recipe in Ads, choose the cadence, avatar/theme, and platforms, then connect the customer’s social account. Every post remains approval-gated until OAuth and an approval policy are in place.";
    }
    if (lower.includes("url blocked") || lower.includes("redirect") || lower.includes("whitelist") || lower.includes("white list")) {
      return `That Meta screen means the callback URL is not approved inside the Facebook developer app yet. Copy this exact URL: ${callbackUrl()} Then go to developers.facebook.com > your app > Facebook Login > Settings, turn on Client OAuth Login and Web OAuth Login, paste it under Valid OAuth Redirect URIs, save, and try Sign in again. If you cannot access that app, mark Meta as Needs keys and paste Page ID, Ad Account ID, and pixel notes so OmniMark can keep helping.`;
    }
    if (lower.includes("invalid scope") || lower.includes("instagram_basic") || lower.includes("instagram basic")) {
      return "Meta is rejecting the Instagram permission. Connect Meta first without instagram_basic, then add Instagram after the Meta app has the right Instagram product/API access and review approval. OmniMark can still connect ads, pages, and business signals first.";
    }
    if (lower.includes("callback")) {
      return `Use this OmniMark callback URL in third-party app settings: ${callbackUrl()} Add it exactly, including https and /app/connections/callback.`;
    }
    if (lower.includes("app id") || lower.includes("client id")) {
      return "For Meta, the App ID is in developers.facebook.com inside the selected app dashboard. For Shopify, the OAuth client ID is in the Shopify app setup. For Google, it is in Google Cloud Console under OAuth Client IDs. If you do not have access, use Connections > Save provider task so the owner can finish it later.";
    }
    if (lower.includes("without oauth") || lower.includes("can't connect") || lower.includes("cannot connect")) {
      return `You are not stuck. For ${businessName}, OmniMark can still use copied product URLs, Shopify /products.json feeds, CSV uploads, Page IDs, Ad Account IDs, screenshots, campaign notes, and manual metrics. Mark the connector Needs keys, copy the setup brief, and keep building pages, ads, and product tests while the provider approval is handled.`;
    }
    if (lower.includes("my value")) {
      return "For My Value of Life: open Connections, choose Meta Marketing API, add your Meta Business/Page notes, mark Needs keys if OAuth is not approved yet, then connect Instagram from Meta Business Suite. Use the setup brief button so the required App ID, Ad Account ID, page, and token steps are saved.";
    }
    if (lower.includes("facebook") || lower.includes("meta")) {
      return `For ${businessName}: choose Meta Marketing API, add the Facebook Page, Ad Account ID, App ID, token/OAuth status, and pixel/dataset notes. If Facebook blocks the redirect, copy ${callbackUrl()} into the Meta app's Valid OAuth Redirect URIs. If Meta rejects instagram_basic, connect Meta first and add Instagram later.`;
    }
    if (lower.includes("shopify")) {
      return "For Shopify: add the store URL, OAuth client ID, and callback URL in the Shopify app settings. If OAuth is not ready, paste the public /products.json feed into Dropshipping so OmniMark can scan products right away.";
    }
    if (lower.includes("tiktok")) {
      return "For TikTok Business: add Advertiser ID, App ID, and the OmniMark callback URL. If approval is pending, save copied campaign metrics or creative notes so OmniMark can still compare hooks, videos, and product angles.";
    }
    if (lower.includes("linkedin")) {
      return `For LinkedIn: use the Marketing Developer Portal OAuth 2.0 flow, request approved organization/ad permissions, and register ${callbackUrl()} as the redirect URI. If access is not approved, save the provider task and use manual metrics until LinkedIn grants access.`;
    }
    if (lower.includes("youtube")) {
      return `For YouTube: create a Google OAuth web client, enable the YouTube Data API, register ${callbackUrl()} exactly, then use the Google connect button.`;
    }
    if (lower.includes("pinterest")) {
      return `For Pinterest: create a developer app, add ${callbackUrl()} as the redirect URI, and request only the board/catalog scopes you need. Keep using content and export tools while review completes.`;
    }
    if (lower === "x" || lower.includes(" x ") || lower.includes("twitter")) {
      return `For X: create an OAuth 2.0 app, add ${callbackUrl()} as the callback URL, and request the minimum scopes required. Save secrets only through Connections.`;
    }
    if (lower.includes("account") || lower.includes("subscription") || lower.includes("billing")) {
      return "For account help: go to Upgrade for subscription status, Members for workspace users, Brand DNA for business setup, and Connections for external accounts. If you are admin, admin tools stay unlocked for your owner email.";
    }
    if (lower.includes("next")) {
      if (location.pathname === "/app/connections") {
        return "Best next step: pick the connector you care about most, press Setup, copy the callback URL, then try Sign in. If the provider blocks you, press Save provider task and keep moving with manual notes or copied URLs.";
      }
      return `Best next step: ${help.body}`;
    }
    if (lower.includes("avatar")) {
      return "For avatars: use Avatar Studio to create a new in-house presenter, then Avatar Library to pick saved avatars for campaigns and videos.";
    }
    if (lower.includes("dropship")) {
      return "For dropshipping: connect Shopify/CJdropshipping first, run the scanner, pick a pre-built theme, then build a product page and video test.";
    }
    return "Tell me the connector, error message, or goal. I can turn it into exact next steps, a fallback path, and what OmniMark can still do while the outside account catches up.";
  }

  function send(text = input) {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((items) => [
      ...items,
      { role: "user", text: trimmed },
      { role: "assistant", text: answer(trimmed) },
    ]);
    setInput("");
    setOpen(true);
  }

  return (
    <div style={wrap} data-testid="omniassist">
      {open && (
        <div style={panel}>
          <div style={header}>
            <div>
              <div style={title}>OmniAssist</div>
              <div style={sub}>{contextLine}</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} style={iconButton} aria-label="Close OmniAssist">x</button>
          </div>
          <div style={pageCard}>
            <strong>{help.title}</strong>
            <span>{help.body}</span>
            {capabilitySummary.length > 0 && (
              <span style={statusNote}>Live status: {capabilitySummary.join(" • ")}</span>
            )}
            {location.pathname === "/app/connections" && (
              <code style={callbackChip}>{callbackUrl()}</code>
            )}
            <div style={actionRow}>
              {help.actions.map((action) => (
                <Link key={action.href} to={action.href} style={miniLink}>{action.label}</Link>
              ))}
            </div>
          </div>
          <div style={messagesBox}>
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} style={message.role === "assistant" ? assistantBubble : userBubble}>
                {message.text}
              </div>
            ))}
          </div>
          <div style={quickGrid}>
            {quickReplies.map((reply) => (
              <button key={reply} type="button" onClick={() => send(reply)} style={quickButton}>{reply}</button>
            ))}
          </div>
          <div style={composer}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send();
              }}
              placeholder="Ask OmniAssist..."
              style={inputStyle}
            />
            <button type="button" onClick={() => send()} style={sendButton}>Send</button>
          </div>
        </div>
      )}
      <button type="button" onClick={() => setOpen((value) => !value)} style={bubble}>
        OmniAssist
      </button>
    </div>
  );
}

const wrap = { position: "fixed", right: 20, bottom: 20, zIndex: 80, display: "grid", justifyItems: "end", gap: 10 };
const bubble = { border: "none", borderRadius: 999, background: "linear-gradient(135deg,#6d5dfc,#8b5cf6)", color: "#fff", padding: "14px 18px", fontWeight: 900, boxShadow: "0 18px 40px rgba(91,71,255,.35)", cursor: "pointer" };
const panel = { width: "min(380px, calc(100vw - 24px))", maxHeight: "min(680px, calc(100vh - 96px))", display: "grid", gridTemplateRows: "auto auto minmax(120px,1fr) auto auto", gap: 10, border: "1px solid rgba(0,0,0,.12)", borderRadius: 12, background: "#fff", boxShadow: "0 24px 70px rgba(15,23,42,.28)", padding: 12 };
const header = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "start" };
const title = { fontWeight: 900, fontSize: 18 };
const sub = { color: "#667085", fontSize: 12, marginTop: 2 };
const iconButton = { width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(0,0,0,.12)", background: "#fff", cursor: "pointer", fontWeight: 900 };
const pageCard = { display: "grid", gap: 6, border: "1px solid #dbeafe", background: "#eff6ff", color: "#1e3a8a", borderRadius: 10, padding: 10, fontSize: 13 };
const callbackChip = { display: "block", border: "1px solid rgba(30,58,138,.16)", background: "#fff", color: "#1e3a8a", borderRadius: 8, padding: 8, fontSize: 11, overflowWrap: "anywhere" };
const actionRow = { display: "flex", flexWrap: "wrap", gap: 6 };
const miniLink = { border: "1px solid rgba(30,58,138,.18)", borderRadius: 8, background: "#fff", color: "#1e3a8a", padding: "6px 8px", fontWeight: 900, fontSize: 12, textDecoration: "none" };
const statusNote = { color: "#92400e", fontSize: 11, fontWeight: 700 };
const messagesBox = { display: "grid", alignContent: "start", gap: 8, overflowY: "auto", padding: "2px 2px 4px" };
const assistantBubble = { justifySelf: "start", maxWidth: "92%", borderRadius: 10, background: "#f2f4f7", color: "#111827", padding: 10, fontSize: 13, lineHeight: 1.35 };
const userBubble = { justifySelf: "end", maxWidth: "92%", borderRadius: 10, background: "#111827", color: "#fff", padding: 10, fontSize: 13, lineHeight: 1.35 };
const quickGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxHeight: 150, overflowY: "auto" };
const quickButton = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 8, textAlign: "left", fontSize: 12, cursor: "pointer" };
const composer = { display: "grid", gridTemplateColumns: "1fr auto", gap: 6 };
const inputStyle = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, padding: "10px 12px", minWidth: 0 };
const sendButton = { border: "none", borderRadius: 8, background: "#111827", color: "#fff", padding: "10px 12px", fontWeight: 900, cursor: "pointer" };
